import React, { useEffect, useState, createRef, useCallback } from 'react';
import Markdown from 'react-markdown';
import { useSelector, useDispatch } from 'react-redux';

import { getMessage, setMessage } from 'redux/reducers/message';
import userCanEdit from 'helpers/userCanEdit';
import useMedia from 'hooks/useMedia';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

import { MediaContent } from '../Message/Message';
import LoadingBar from 'components/styled/LoadingBar/LoadingBar';
import LinkRenderer from 'components/utility/LinkRenderer/LinkRenderer';
import HeadingRenderer from 'components/utility/HeadingRenderer/HeadingRenderer';
import ErrorDisplay from 'components/Error/ErrorDisplay/ErrorDisplay';
import Box from 'components/elements/Box';
import Span from 'components/elements/span';

import { RootState } from 'redux/rootReducer';

import { ReactComponent as PointerIcon } from 'icons/diagonal-arrow-icon.svg';

import {
  Button,
  SquareButton,
  H2,
  LoadingBarWrapper,
  ReadMoreButton,
  StyledMain,
  StyledLink,
  TextArea,
} from './EditableMessage.style';

import colors from 'styles/themes/colors';

const EditableMessage: React.FC<{ messageId: string }> = ({ messageId }) => {
  const dispatch = useDispatch();
  const userCanChange = userCanEdit(
    useSelector((state: RootState) => state?.authentication?.profile)
  );
  const getOriginalMessage = useCallback(
    (state: any) => state.message[messageId],
    [messageId]
  );
  const {
    loading,
    error,
    errorInfo,
    value: messageValue,
  } = useSelector(getOriginalMessage) || {};
  const [editMode, setMode] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isSmallScreen = useMedia('(max-width: 40em)');

  useEffect(() => {
    /** Load the message via Redux when it is not yet set. */
    if (!messageValue) {
      dispatch(getMessage(messageId));
    }
  }, [dispatch, messageId, messageValue]);

  /** Check if the markdown string includes --- which equals to <hr /> */
  const hasReadMore = messageValue && messageValue.includes('---');
  const textArea = createRef();

  if (!messageValue) return null;

  const saveMessage = (): void => {
    userCanChange &&
      textArea.current &&
      dispatch(
        setMessage(messageId, (textArea as any)?.current?.value || null)
      ).then((res: any) => {
        if (!res.error) {
          setMode(false);
        }
      });
  };

  const LinkRendererComponent: React.FC<any> = (props: any) => (
    <LinkRenderer {...props} />
  );

  const openSystemStatusPage = (): void => {
    logToAmplitude('Opus System Status button Clicked', {
      type: 'System Status',
    });
  };

  return (
    <Box
      backgroundColor="white"
      p={isSmallScreen ? '1.9rem 1.4rem' : '1.4rem 2.2rem'}
      data-pw="Status Update section"
    >
      <LoadingBarWrapper>
        <LoadingBar
          loading={loading || false}
          backgroundGradientColor="white"
        />
      </LoadingBarWrapper>

      <Box
        display="flex"
        alignItems={isSmallScreen ? 'flex-start' : 'center'}
        justifyContent="space-between"
        mb={isSmallScreen ? (editMode ? '2rem' : 0) : editMode ? 4 : '-10px'}
      >
        <Box
          display="flex"
          alignItems={isSmallScreen && editMode ? 'flex-start' : 'center'}
          flexDirection={isSmallScreen && editMode ? 'column' : 'row'}
        >
          <H2 as="h2" bottomMargin={isSmallScreen && editMode ? '0.8rem' : '0'}>
            Status Update
          </H2>

          {userCanChange &&
            (editMode ? (
              <Box ml={editMode && !isSmallScreen ? 5 : 0}>
                <SquareButton onClick={saveMessage}>Save</SquareButton>
                <Button
                  type="button"
                  color={colors.DisabledGrey}
                  onClick={(): void => setMode(false)}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Button
                type="button"
                color={colors.AccentGreen}
                onClick={(): void => setMode(true)}
              >
                Edit
              </Button>
            ))}
        </Box>

        <StyledLink
          href="/systemStatus"
          rel="noopener noreferrer"
          onClick={openSystemStatusPage}
        >
          <Box as="span" display="flex" alignItems="center">
            <Span marginRight={2} fontSize="0.875rem" fontWeight="600">
              {isSmallScreen ? '' : 'Opus '}System Status
            </Span>
            <PointerIcon />
          </Box>
        </StyledLink>
      </Box>
      <>
        <MediaContent isEditMode={editMode}>
          {editMode ? (
            <>
              {error && <ErrorDisplay error={error} errorInfo={errorInfo} />}
              <TextArea rows="15" defaultValue={messageValue} ref={textArea} />
            </>
          ) : (
            <>
              {error ? (
                <ErrorDisplay error={error} errorInfo={errorInfo} />
              ) : (
                <StyledMain expanded={expanded}>
                  <Markdown
                    source={messageValue}
                    allowedTypes={[
                      'text',
                      'root',
                      'break',
                      'paragraph',
                      'emphasis',
                      'strong',
                      'link',
                      'list',
                      'listItem',
                      'heading',
                      'thematicBreak',
                    ]}
                    renderers={{
                      link: LinkRendererComponent,
                      heading: HeadingRenderer,
                    }}
                  />
                  {hasReadMore && (
                    <ReadMoreButton
                      expanded={expanded}
                      onClick={(): void => setExpanded(!expanded)}
                    >
                      {expanded ? 'Collapse' : 'Read more'}
                    </ReadMoreButton>
                  )}
                </StyledMain>
              )}
            </>
          )}
        </MediaContent>
      </>
    </Box>
  );
};

export default EditableMessage;
